import React from "react";
import {
  Box,
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

const SKELETON_ROWS = 6;

const hdSx = {
  fontFamily: "Heebo",
  fontWeight: 600,
  color: "#362B24",
  fontSize: "13px",
  whiteSpace: "nowrap",
  bgcolor: "#f9fafb",
  borderBottom: "1px solid #EAEAEA",
  py: 1.5,
  px: 2,
};

const bdSx = {
  fontFamily: "Heebo",
  color: "#362B24",
  fontSize: "13px",
  py: 1.25,
  px: 2,
};

/**
 * AdminTable — centralized reusable table for the admin project.
 *
 * Props:
 *   columns      — array of column config objects (see below)
 *   rows         — data array
 *   loading      — boolean: shows skeleton rows
 *   rowKey       — string key for unique row id (default "_id")
 *   emptyText    — empty state message
 *   page         — current page, 1-indexed (from Redux)
 *   rowsPerPage  — rows per page (default 20)
 *   totalCount   — total record count for pagination
 *   onPageChange — (page: number) => void, receives 1-indexed page
 *   selected     — array of selected row ids
 *   onSelect     — (id, checked) => void — enables checkbox column
 *   onSelectAll  — (checked) => void
 *   deletingId   — id of row currently being deleted (dims that row)
 *   rowSx        — (row) => sx object — additional per-row sx
 *
 * Column config:
 *   { field, headerName, width, minWidth, align, cellSx, headerSx, renderCell }
 *   - renderCell(row) => ReactNode   custom cell renderer
 *   - field is used as fallback: row[field] ?? "—"
 */
const AdminTable = ({
  columns = [],
  rows = [],
  loading = false,
  rowKey = "_id",
  emptyText = "No data found",
  page = 1,
  rowsPerPage = 20,
  totalCount = 0,
  onPageChange,
  selected = [],
  onSelect,
  onSelectAll,
  deletingId,
  rowSx,
}) => {
  const hasCheckbox = !!onSelect;
  const allChecked = rows.length > 0 && selected.length === rows.length;
  const indeterminate = selected.length > 0 && !allChecked;
  const colSpan = columns.length + (hasCheckbox ? 1 : 0);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)",
        border: "1px solid #F4F4F4",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {hasCheckbox && (
                <TableCell padding="checkbox" sx={{ ...hdSx, pl: 1.5 }}>
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allChecked}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    size="small"
                    sx={{
                      color: "#CBCBCB",
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#065F46" },
                    }}
                  />
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell
                  key={col.field}
                  align={col.align || "left"}
                  sx={{
                    ...hdSx,
                    width: col.width,
                    minWidth: col.minWidth,
                    ...(col.headerSx || {}),
                  }}
                >
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <TableRow key={`sk${i}`}>
                  {hasCheckbox && (
                    <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                      <Skeleton
                        variant="rectangular"
                        width={16}
                        height={16}
                        sx={{ borderRadius: "3px", mx: "auto" }}
                      />
                    </TableCell>
                  )}
                  {columns.map((col, j) => (
                    <TableCell key={j} sx={{ px: 2 }}>
                      <Skeleton variant="text" width="65%" height={16} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ py: 8, textAlign: "center", border: "none" }}>
                  <Typography sx={{ fontFamily: "Heebo", color: "#ABABAB", fontSize: "14px" }}>
                    {emptyText}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const id = row[rowKey];
                const isDeleting = deletingId === id;
                const isSelected = selected.includes(id);
                const extra = rowSx ? rowSx(row) : {};
                return (
                  <TableRow
                    key={id}
                    selected={isSelected}
                    hover
                    sx={{
                      opacity: isDeleting ? 0.45 : 1,
                      pointerEvents: isDeleting ? "none" : "auto",
                      transition: "opacity 0.2s",
                      "&.Mui-selected": { bgcolor: "rgba(6,95,70,0.03)" },
                      "&.Mui-selected:hover": { bgcolor: "rgba(6,95,70,0.05)" },
                      ...extra,
                    }}
                  >
                    {hasCheckbox && (
                      <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => onSelect?.(id, e.target.checked)}
                          size="small"
                          sx={{
                            color: "#CBCBCB",
                            "&.Mui-checked": { color: "#065F46" },
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell
                        key={col.field}
                        align={col.align || "left"}
                        sx={{ ...bdSx, ...(col.cellSx || {}) }}
                      >
                        {col.renderCell ? col.renderCell(row) : (row[col.field] ?? "—")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > rowsPerPage && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
          onPageChange={(_, pg) => onPageChange?.(pg + 1)}
          sx={{
            borderTop: "1px solid #F4F4F4",
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontFamily: "Heebo",
              fontSize: "13px",
              my: 0,
            },
            "& .MuiTablePagination-actions button": { color: "#362B24" },
          }}
        />
      )}
    </Paper>
  );
};

export default AdminTable;
